import { Box, Col, Image, Text } from '@opendoor/bricks-next';
import { EntryComponent } from 'cms/entries/entries';
import { IFcTestimonialCarouselCard, ILpComponentPhotoTestimonials } from 'declarations/contentful';
import Head from 'next/head';

import Grid, { gridStyles } from './Grid';
import { resizeTestimonialAvatarImageUrl } from './shared/utils';

const DESKTOP_CARD_ROW_MEDIA = '(min-width: 1201px)';

const getAvatarImageUrl = (entry: IFcTestimonialCarouselCard) => {
  const imageUrl = entry.fields.image?.fields?.file?.url;
  return imageUrl ? resizeTestimonialAvatarImageUrl(imageUrl) : null;
};

const TestimonialCard = ({ entry }: { entry: IFcTestimonialCarouselCard }) => {
  const { fields } = entry;
  const resizedImageUrl = getAvatarImageUrl(entry);

  return (
    <Col
      flex={1}
      borderRadius={36}
      p={36}
      backgroundColor="$backgroundTertiary"
      alignItems="center"
      gap="$12x"
      $smallerThanMD={{ p: '$12x' }}
    >
      {resizedImageUrl && (
        <Box
          width={112}
          height={112}
          $smallerThanMD={{ width: 80, height: 80 }}
          borderRadius="$full"
          overflow="hidden"
        >
          <Image
            source={{ uri: resizedImageUrl }}
            alt={fields.name}
            width={112}
            height={112}
            $smallerThanMD={{ width: 80, height: 80 }}
            $platform-web={{ objectFit: 'cover' }}
          />
        </Box>
      )}
      <Text tag="p" typography="$bodyMedium" color="$contentPrimary" textAlign="center">
        &ldquo;{fields.quote}&rdquo;
      </Text>
      <Col gap="$2x" alignItems="center">
        <Text tag="p" typography="$labelLarge" color="$contentPrimary">
          {fields.name}
        </Text>
        <Text tag="p" typography="$labelSmall" color="$contentTertiary">
          {fields.locationContext}
        </Text>
      </Col>
    </Col>
  );
};

const RenderPhotoTestimonials = (entry: ILpComponentPhotoTestimonials) => {
  const { fields } = entry;
  const cards = fields.cards ?? [];
  const avatarImageUrls = Array.from(
    new Set(cards.map(getAvatarImageUrl).filter((url): url is string => Boolean(url))),
  );

  return (
    <Col
      analyticsName={`cosmos-landing-page-photo-testimonials-${
        fields?.analyticsId ?? entry.sys.id
      }`}
      trackImpression
    >
      <Head>
        {avatarImageUrls.map((url, index) => (
          <link
            key={`photo-testimonials-avatar-${url}`}
            rel="preload"
            as="image"
            href={url}
            media={index === 0 ? undefined : DESKTOP_CARD_ROW_MEDIA}
            fetchPriority="high"
          />
        ))}
      </Head>
      <Grid py="$32x" $smallerThanMD={{ py: '$16x' }}>
        <Col $platform-web={{ gridColumn: gridStyles.colSpan12 }} gap="$20x">
          {/* Header */}
          <Col gap="$6x">
            <Text tag="p" typography="$labelSmall" color="$contentAccentBrand">
              {fields.eyebrow}
            </Text>
            <Col gap="$4x">
              <Text tag="h2" typography="$headerSmall" color="$contentPrimary">
                {fields.counterTarget
                  ? `${fields.counterTarget.toLocaleString('en-US')} ${fields.title}`
                  : fields.title}
              </Text>
              <Text tag="p" typography="$bodyMedium" color="$contentTertiary">
                {fields.description}
              </Text>
            </Col>
          </Col>

          {/* Cards: row on desktop, column on mobile */}
          <Box flexDirection="row" gap="$10x" $smallerThanLG={{ flexDirection: 'column' }}>
            {cards.map((card: IFcTestimonialCarouselCard) => (
              <Box key={card.sys.id} flex={1} minWidth={280} $smallerThanLG={{ minWidth: 'auto' }}>
                <TestimonialCard entry={card} />
              </Box>
            ))}
          </Box>
        </Col>
      </Grid>
    </Col>
  );
};

const PhotoTestimonials: EntryComponent<ILpComponentPhotoTestimonials> = {
  render: RenderPhotoTestimonials,
};

export default PhotoTestimonials;
